import { ReactNode, useCallback } from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import { UrlFilters } from '../../hooks/useUrlFilters';
import { Totals } from '../../types';
import { StatusChip } from '../Chip';
import {
  TREE_NODE,
  TEST_RESULTS_TABLE_FILTERS,
} from '../../constants/urlParams';

type Props = {
  id?: string;
  columnName?: string;
  totals?: Totals;
  size?: 'small' | 'medium';
  navigate?: (url: string) => void;
  formatUrlParam?: (modelId?: string) => string | undefined;
  qualityDimension?: string;
  tooltips?: Record<keyof Totals, string | ReactNode>;
  hideEmptyState?: boolean;
};

const TestResultsChips = ({
  id,
  columnName,
  totals,
  size,
  navigate,
  formatUrlParam,
  qualityDimension,
  tooltips,
  hideEmptyState,
}: Props) => {
  const onChipClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, status: string[]) => {
      if (formatUrlParam && navigate) {
        e.stopPropagation();

        const newParams = new URLSearchParams();
        const urlParam = formatUrlParam(id);

        const tableFilters: UrlFilters = [
          {
            property: 'latestRunStatus',
            operator: 'equals',
            value: status,
          },
        ];

        if (columnName) {
          tableFilters.push({
            property: 'columnName',
            operator: 'equals',
            value: [columnName],
          });
        }

        if (qualityDimension) {
          tableFilters.push({
            property: 'qualityDimension',
            operator: 'equals',
            value: [qualityDimension],
          });
        }

        if (urlParam) {
          newParams.set(TREE_NODE, urlParam);
          newParams.set(
            TEST_RESULTS_TABLE_FILTERS,
            JSON.stringify(tableFilters),
          );

          navigate?.(`/report/test-results?${newParams?.toString()}`);
        }
      }
    },
    [formatUrlParam, navigate, id, columnName, qualityDimension],
  );

  const isEmpty =
    !totals ||
    Object.keys(totals).every((key) => !totals?.[key as keyof Totals]);

  if (isEmpty) {
    if (!hideEmptyState) {
      return <Typography>-</Typography>;
    }
    return null;
  }

  return (
    <Stack direction="row" sx={{ gap: 1 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.6 }}>
        {totals?.passed > 0 && (
          <Tooltip title={tooltips?.passed}>
            <div>
              <StatusChip
                sx={navigate ? { cursor: 'pointer' } : {}}
                onClick={(e) =>
                  navigate ? onChipClick(e, ['pass']) : undefined
                }
                clickable={!!navigate}
                label={totals.passed}
                status="success"
                size={size}
              />
            </div>
          </Tooltip>
        )}
        {totals?.warnings > 0 && (
          <Tooltip title={tooltips?.warnings}>
            <div>
              <StatusChip
                sx={navigate ? { cursor: 'pointer' } : {}}
                onClick={(e) =>
                  navigate ? onChipClick(e, ['warn']) : undefined
                }
                clickable={!!navigate}
                label={totals.warnings}
                status="warning"
                size={size}
              />
            </div>
          </Tooltip>
        )}
        {(totals?.errors > 0 || totals?.failures > 0) && (
          <Tooltip title={tooltips?.errors || tooltips?.failures}>
            <div>
              <StatusChip
                sx={navigate ? { cursor: 'pointer' } : {}}
                onClick={(e) =>
                  navigate ? onChipClick(e, ['error', 'fail']) : undefined
                }
                clickable={!!navigate}
                label={(totals.errors || 0) + (totals.failures || 0)}
                status="error"
                size={size}
              />
            </div>
          </Tooltip>
        )}
        {totals?.noData > 0 && (
          <Tooltip title={tooltips?.noData}>
            <div>
              <StatusChip
                sx={navigate ? { cursor: 'pointer' } : {}}
                onClick={(e) =>
                  navigate ? onChipClick(e, ['no_data', 'skipped']) : undefined
                }
                clickable={!!navigate}
                label={totals.noData}
                status="noData"
                size={size}
              />
            </div>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export default TestResultsChips;
